import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, 
  Calendar as CalendarIcon,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Download,
  Clock,
  CreditCard,
  Percent,
  Coffee,
  PieChart as PieChartIcon,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  GitCompare
} from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, AreaChart, Area, Legend 
} from 'recharts';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

// Comparison presets
const COMPARISON_PRESETS = [
  { value: 'none', label: 'Sin comparación' },
  { value: 'previous_period', label: 'Período anterior' },
  { value: 'previous_month', label: 'Mes anterior' },
  { value: 'previous_year', label: 'Mismo período año anterior' },
  { value: 'custom', label: 'Personalizado...' }
];

export default function Reports() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date()
  });
  const [report, setReport] = useState(null);
  const [comparisonType, setComparisonType] = useState('none');
  const [customCompareRange, setCustomCompareRange] = useState({ from: null, to: null });
  const [showCompareCalendar, setShowCompareCalendar] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [dateRange, comparisonType, customCompareRange]);

  const getComparisonDates = () => {
    if (!dateRange.from || !dateRange.to) return null;
    
    const daysDiff = Math.ceil((dateRange.to - dateRange.from) / (1000 * 60 * 60 * 24));
    
    switch (comparisonType) {
      case 'previous_period':
        return {
          start: format(subDays(dateRange.from, daysDiff), 'yyyy-MM-dd'),
          end: format(subDays(dateRange.from, 1), 'yyyy-MM-dd')
        };
      case 'previous_month':
        const prevMonth = subMonths(dateRange.from, 1);
        return {
          start: format(startOfMonth(prevMonth), 'yyyy-MM-dd'),
          end: format(endOfMonth(prevMonth), 'yyyy-MM-dd')
        };
      case 'previous_year':
        return {
          start: format(subMonths(dateRange.from, 12), 'yyyy-MM-dd'),
          end: format(subMonths(dateRange.to, 12), 'yyyy-MM-dd')
        };
      case 'custom':
        if (customCompareRange.from && customCompareRange.to) {
          return {
            start: format(customCompareRange.from, 'yyyy-MM-dd'),
            end: format(customCompareRange.to, 'yyyy-MM-dd')
          };
        }
        return null;
      default:
        return null;
    }
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.from) params.append('start_date', dateRange.from.toISOString().split('T')[0]);
      if (dateRange.to) params.append('end_date', dateRange.to.toISOString().split('T')[0]);
      
      const compareDates = getComparisonDates();
      if (compareDates) {
        params.append('compare_start', compareDates.start);
        params.append('compare_end', compareDates.end);
      }
      
      const response = await axios.get(`${API}/reports/sales?${params}`, { withCredentials: true });
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error al cargar reporte');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

  const formatCurrency = (value) => `$${(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  
  const formatHour = (hour) => `${hour}:00`;

  const ChangeIndicator = ({ value, inverse = false }) => {
    if (value === undefined || value === null) return null;
    
    const isPositive = inverse ? value < 0 : value > 0;
    const isNegative = inverse ? value > 0 : value < 0;
    const isNeutral = value === 0;
    
    return (
      <div className={`flex items-center gap-1 text-sm font-medium ${
        isPositive ? 'text-emerald-600 dark:text-emerald-400' :
        isNegative ? 'text-red-600 dark:text-red-400' :
        'text-muted-foreground'
      }`}>
        {isPositive && <ArrowUpRight className="w-4 h-4" />}
        {isNegative && <ArrowDownRight className="w-4 h-4" />}
        {isNeutral && <Minus className="w-4 h-4" />}
        <span>{Math.abs(value)}%</span>
      </div>
    );
  };

  const exportCSV = () => {
    if (!report?.top_products?.length) {
      toast.error('No hay datos para exportar');
      return;
    }
    const headers = ['Producto', 'Cantidad', 'Ingresos'];
    const rows = report.top_products.map(p => [p.name, p.quantity, p.revenue.toFixed(2)]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_ventas_${format(dateRange.from, 'yyyyMMdd')}_${format(dateRange.to, 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Reporte exportado');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando reportes...</p>
        </div>
      </div>
    );
  }

  const comparison = report?.comparison;
  const changes = comparison?.changes;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-semibold">Reportes y Análisis</h1>
            <p className="text-sm text-muted-foreground">
              Dashboard de rendimiento del negocio
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Exportar</span>
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="date-range-btn">
                <CalendarIcon className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">
                  {dateRange.from ? format(dateRange.from, 'dd MMM', { locale: es }) : 'Inicio'} - {' '}
                  {dateRange.to ? format(dateRange.to, 'dd MMM', { locale: es }) : 'Fin'}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                initialFocus
                mode="range"
                selected={dateRange}
                onSelect={(range) => setDateRange(range || { from: null, to: null })}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Comparison Selector */}
        <Card className="bg-gradient-to-r from-violet-500/5 to-purple-500/5 border-violet-200 dark:border-violet-900">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                  <GitCompare className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <p className="font-semibold text-sm">Comparar con</p>
                  <p className="text-xs text-muted-foreground">Analiza el cambio respecto a otro período</p>
                </div>
              </div>
              
              <div className="flex-1 flex flex-col sm:flex-row gap-3">
                <Select value={comparisonType} onValueChange={(v) => {
                  setComparisonType(v);
                  if (v === 'custom') setShowCompareCalendar(true);
                }}>
                  <SelectTrigger className="w-full sm:w-[220px]" data-testid="comparison-select">
                    <SelectValue placeholder="Seleccionar comparación" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPARISON_PRESETS.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {comparisonType === 'custom' && (
                  <Popover open={showCompareCalendar} onOpenChange={setShowCompareCalendar}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full sm:w-auto">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {customCompareRange.from && customCompareRange.to ? (
                          `${format(customCompareRange.from, 'dd/MM')} - ${format(customCompareRange.to, 'dd/MM')}`
                        ) : (
                          'Elegir fechas'
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        selected={customCompareRange}
                        onSelect={(range) => {
                          setCustomCompareRange(range || { from: null, to: null });
                          if (range?.from && range?.to) setShowCompareCalendar(false);
                        }}
                        numberOfMonths={2}
                      />
                    </PopoverContent>
                  </Popover>
                )}

                {comparison && (
                  <Badge variant="outline" className="self-center whitespace-nowrap">
                    vs {comparison.period}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards - Row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-200 dark:border-emerald-900" data-testid="total-sales-card">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Ventas Totales</p>
                    <p className="text-2xl font-bold font-mono text-emerald-700 dark:text-emerald-300">
                      {formatCurrency(report?.total_sales)}
                    </p>
                    {comparison && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Antes: {formatCurrency(comparison.total_sales)}
                      </p>
                    )}
                  </div>
                </div>
                {changes && <ChangeIndicator value={changes.sales} />}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="total-orders-card">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Órdenes</p>
                    <p className="text-2xl font-bold">{report?.total_orders || 0}</p>
                    {comparison && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Antes: {comparison.total_orders}
                      </p>
                    )}
                  </div>
                </div>
                {changes && <ChangeIndicator value={changes.orders} />}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="avg-ticket-card">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Ticket Promedio</p>
                    <p className="text-2xl font-bold font-mono">
                      {formatCurrency(report?.average_order_value)}
                    </p>
                    {comparison && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Antes: {formatCurrency(comparison.average_order_value)}
                      </p>
                    )}
                  </div>
                </div>
                {changes && <ChangeIndicator value={changes.avg_order} />}
              </div>
            </CardContent>
          </Card>

          <Card data-testid="tips-card">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <Percent className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Propinas</p>
                    <p className="text-2xl font-bold font-mono">
                      {formatCurrency(report?.total_tips)}
                    </p>
                    {comparison && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Antes: {formatCurrency(comparison.total_tips)}
                      </p>
                    )}
                  </div>
                </div>
                {changes && <ChangeIndicator value={changes.tips} />}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Cards - Row 2 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Impuestos</p>
                  <p className="text-lg font-bold font-mono">{formatCurrency(report?.total_tax)}</p>
                </div>
                <Badge variant="outline" className="text-xs">IVA</Badge>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Servicio</p>
                  <p className="text-lg font-bold font-mono">{formatCurrency(report?.total_service_charge)}</p>
                </div>
                <Badge variant="outline" className="text-xs">%</Badge>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Espacios</p>
                  <p className="text-lg font-bold">{report?.sales_by_space?.length || 0}</p>
                </div>
                <Badge variant="outline" className="text-xs">Activos</Badge>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Categorías</p>
                  <p className="text-lg font-bold">{report?.sales_by_category?.length || 0}</p>
                </div>
                <Badge variant="outline" className="text-xs">Vendidas</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="overview" data-testid="tab-overview">General</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Productos</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-timeline">Tiempo</TabsTrigger>
            <TabsTrigger value="details" data-testid="tab-details">Detalles</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Sales by Space */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5 text-primary" />
                    Ventas por Espacio
                  </CardTitle>
                  <CardDescription>Distribución de ingresos por zona</CardDescription>
                </CardHeader>
                <CardContent>
                  {report?.sales_by_space?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={report.sales_by_space}
                          dataKey="total"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          innerRadius={60}
                          paddingAngle={2}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                          {report.sales_by_space.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No hay datos suficientes</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sales by Category */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Coffee className="w-5 h-5 text-primary" />
                    Ventas por Categoría
                  </CardTitle>
                  <CardDescription>Rendimiento por tipo de producto</CardDescription>
                </CardHeader>
                <CardContent>
                  {report?.sales_by_category?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={report.sales_by_category.slice(0, 6)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                        <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        />
                        <Bar dataKey="total" fill="#10B981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      <div className="text-center">
                        <Coffee className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No hay datos suficientes</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Payment Methods */}
            {report?.payment_methods?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    Métodos de Pago
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {report.payment_methods.map((pm, idx) => (
                      <div key={idx} className="p-4 rounded-xl bg-muted/50 text-center">
                        <p className="text-2xl font-bold">{pm.count}</p>
                        <p className="text-sm text-muted-foreground capitalize">
                          {pm.method === 'cash' ? 'Efectivo' : pm.method === 'card' ? 'Tarjeta' : pm.method === 'transfer' ? 'Transferencia' : pm.method}
                        </p>
                        <p className="text-xs font-mono mt-1">{formatCurrency(pm.total)}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-6 mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top 5 Productos</CardTitle>
                  <CardDescription>Por ingresos generados</CardDescription>
                </CardHeader>
                <CardContent>
                  {report?.top_products?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={report.top_products.slice(0, 5)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                        />
                        <Bar dataKey="revenue" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No hay datos suficientes
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Más Vendidos</CardTitle>
                  <CardDescription>Por cantidad de unidades</CardDescription>
                </CardHeader>
                <CardContent>
                  {report?.top_products?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={[...report.top_products].sort((a, b) => b.quantity - a.quantity).slice(0, 5)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                        <Bar dataKey="quantity" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      No hay datos suficientes
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Evolución de Ventas
                </CardTitle>
                <CardDescription>Ingresos diarios en el período seleccionado</CardDescription>
              </CardHeader>
              <CardContent>
                {report?.sales_timeline?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={report.sales_timeline}>
                      <defs>
                        <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(d) => format(new Date(d), 'dd/MM', { locale: es })}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tickFormatter={(v) => formatCurrency(v)} />
                      <Tooltip 
                        labelFormatter={(d) => format(new Date(d), 'dd MMMM yyyy', { locale: es })}
                        formatter={(value, name) => [formatCurrency(value), name === 'total' ? 'Ventas' : 'Órdenes']}
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                      />
                      <Area type="monotone" dataKey="total" stroke="#10B981" fillOpacity={1} fill="url(#colorSales)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No hay datos de ventas en este período</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Actividad por Hora
                </CardTitle>
                <CardDescription>Distribución de órdenes durante el día</CardDescription>
              </CardHeader>
              <CardContent>
                {report?.orders_by_hour?.some(h => h.orders > 0) ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={report.orders_by_hour.filter(h => h.hour >= 8 && h.hour <= 23)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="hour" tickFormatter={formatHour} tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={(h) => `${formatHour(h)} - ${formatHour(h + 1)}`}
                        formatter={(value, name) => [value, name === 'orders' ? 'Órdenes' : formatCurrency(value)]}
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                      />
                      <Bar dataKey="orders" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    <div className="text-center">
                      <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No hay datos de actividad</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Detalle de Productos</CardTitle>
                <CardDescription>Rendimiento completo por producto</CardDescription>
              </CardHeader>
              <CardContent>
                {report?.top_products?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 font-semibold text-muted-foreground">#</th>
                          <th className="text-left py-3 px-4 font-semibold text-muted-foreground">Producto</th>
                          <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Cantidad</th>
                          <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Ingresos</th>
                          <th className="text-right py-3 px-4 font-semibold text-muted-foreground">% Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.top_products.map((product, index) => (
                          <tr key={index} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                            <td className="py-3 px-4 text-muted-foreground">{index + 1}</td>
                            <td className="py-3 px-4 font-medium">{product.name}</td>
                            <td className="py-3 px-4 text-right font-mono">{product.quantity}</td>
                            <td className="py-3 px-4 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                              {formatCurrency(product.revenue)}
                            </td>
                            <td className="py-3 px-4 text-right">
                              <Badge variant="outline">
                                {((product.revenue / (report.total_sales || 1)) * 100).toFixed(1)}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-muted/30">
                          <td colSpan="2" className="py-3 px-4 font-bold">Total</td>
                          <td className="py-3 px-4 text-right font-mono font-bold">
                            {report.top_products.reduce((sum, p) => sum + p.quantity, 0)}
                          </td>
                          <td className="py-3 px-4 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                            {formatCurrency(report.top_products.reduce((sum, p) => sum + p.revenue, 0))}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="py-12 text-center text-muted-foreground">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay ventas en este período</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {report?.sales_by_space?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Detalle por Espacio</CardTitle>
                  <CardDescription>Rendimiento de cada zona del negocio</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 font-semibold text-muted-foreground">Espacio</th>
                          <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Órdenes</th>
                          <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Ingresos</th>
                          <th className="text-right py-3 px-4 font-semibold text-muted-foreground">Ticket Prom.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.sales_by_space.map((space, index) => (
                          <tr key={index} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                            <td className="py-3 px-4 font-medium">{space.name}</td>
                            <td className="py-3 px-4 text-right font-mono">{space.orders}</td>
                            <td className="py-3 px-4 text-right font-mono font-bold text-blue-600 dark:text-blue-400">
                              {formatCurrency(space.total)}
                            </td>
                            <td className="py-3 px-4 text-right font-mono">
                              {formatCurrency(space.orders > 0 ? space.total / space.orders : 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
